import React, { useState, useMemo } from 'react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  InfiniteScroll,
  LoadingSpinner,
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
  TruncatedText,
} from '@ais-ui/ui-core';
import { PiAnchorThin } from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { usePortsFilter } from './usePortsFilter';
import { PortList } from '@ais-ui/api-spec/portserver';
import { useLocalization } from '@ais-ui/locale-management';

interface PortSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  form?: any; // Form instance to update port data
}

export function PortSelect({
  value,
  onValueChange,
  placeholder = 'Select a port...',
  disabled = false,
  form,
}: PortSelectProps) {
  const { messages } = useLocalization();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { filteredPorts, allPorts, isLoading } = usePortsFilter({
    searchString: search,
    maxResults: 1000, // Higher limit since we're using infinite scroll
  });

  // Show all ports when no search term, filtered ports when searching (3+ chars), or empty when searching but less than 3 chars
  const displayPorts = useMemo(() => {
    if (search.length === 0) {
      // No search - show all ports
      return allPorts;
    } else if (search.length < 3) {
      // Searching but less than 3 characters - show empty
      return [];
    } else {
      // Searching with 3+ characters - show filtered results
      return filteredPorts;
    }
  }, [search.length, allPorts, filteredPorts]);

  // Find the selected port for display
  const selectedPort = useMemo(() => {
    if (!value) return null;
    return allPorts.find((port) => port.id.toString() === value);
  }, [value, allPorts]);

  // Calculate dynamic height based on content
  const calculatedHeight = useMemo(() => {
    const COMMAND_INPUT_HEIGHT = 40;
    const COMMAND_ITEM_HEIGHT = 60;
    const LOADING_SPINNER_HEIGHT = 68;
    const COMMAND_EMPTY_HEIGHT = 68;
    const SEARCH_MESSAGE_HEIGHT = 68;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 300;
    const NONE_ITEM_HEIGHT = 40;

    let contentHeight =
      COMMAND_INPUT_HEIGHT + PADDING_AND_BORDERS + NONE_ITEM_HEIGHT;

    if (isLoading) {
      contentHeight += LOADING_SPINNER_HEIGHT;
    } else if (search.length > 0 && search.length < 3) {
      // Show "Enter three or more characters" message
      contentHeight += SEARCH_MESSAGE_HEIGHT;
    } else if (displayPorts.length === 0) {
      contentHeight += COMMAND_EMPTY_HEIGHT;
    } else {
      // InfiniteScroll will handle the rendering, so we can use max height
      contentHeight = MAX_HEIGHT;
    }

    return Math.min(contentHeight, MAX_HEIGHT);
  }, [displayPorts.length, isLoading, search.length]);

  const handlePortSelect = (port: PortList | null) => {
    if (port) {
      onValueChange(port.id.toString());
      // Also update the form with the complete port data
      if (form && form.setValue) {
        form.setValue('favorite_port_data', {
          id: port.id,
          name: port.name,
          country: port.country,
          locode: port.locode,
          position: port.position,
        });
      }
    } else {
      onValueChange('');
      // Clear the port data when "None" is selected
      if (form && form.setValue) {
        form.setValue('favorite_port_data', undefined);
      }
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          className={cn('w-full justify-between pr-3')}
        >
          {selectedPort ? (
            <div className="flex items-center">
              <PiAnchorThin className="mr-2 size-4" />
              <TruncatedText
                text={`${selectedPort.name} (${selectedPort.locode})`}
                className="text-left"
              />
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContentNoPortal
        align="start"
        className="w-full p-0 overflow-hidden"
        style={{ height: `${calculatedHeight}px` }}
      >
        <Command shouldFilter={false} className="flex flex-col h-full">
          <CommandInput
            placeholder="Search ports..."
            onValueChange={setSearch}
            value={search}
          />

          {/* None option - always visible */}
          <div className="p-1 border-b">
            <CommandItem
              onSelect={() => handlePortSelect(null)}
              className="cursor-pointer"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  <div className="mr-2 size-4" />{' '}
                  {/* Empty space for icon alignment */}
                  <span className="text-muted-foreground">None</span>
                </div>
                <Check
                  className={cn('size-4', !value ? 'opacity-100' : 'opacity-0')}
                />
              </div>
            </CommandItem>
          </div>

          {/* Port results - always shown */}
          {isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <LoadingSpinner size="small" />
            </div>
          )}

          {!isLoading && (
            <>
              <CommandEmpty>{messages.portSelectNoResults}</CommandEmpty>
              {displayPorts.length > 0 && (
                <InfiniteScroll
                  items={displayPorts}
                  limit={30}
                  observeResize={true}
                  renderItem={(port: PortList) => (
                    <CommandItem
                      key={port.id}
                      onSelect={() => handlePortSelect(port)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex flex-col space-y-1 min-w-0 flex-1">
                          <div className="flex items-center">
                            <PiAnchorThin className="mr-2 size-4 flex-shrink-0" />
                            <TruncatedText text={port.name} maxWidth="200px" />
                          </div>
                          <div className="flex space-x-2 text-xs text-muted-foreground items-center ml-6">
                            <span>Locode: {port.locode}</span>
                            <div className="rounded-full h-1 w-1 bg-blue-600" />
                            <span>Country: {port.country}</span>
                          </div>
                        </div>
                        <Check
                          className={cn(
                            'ml-2 size-4 flex-shrink-0',
                            value === port.id.toString()
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                      </div>
                    </CommandItem>
                  )}
                  className="flex-1 min-h-0 p-1"
                />
              )}
              {search.length > 0 && search.length < 3 && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Enter three or more characters to search.
                </div>
              )}
            </>
          )}
        </Command>
      </PopoverContentNoPortal>
    </Popover>
  );
}
